import { BillingLicensePlans } from '@kizen/api/billing';
import { Icon } from '@kizen/kds/Icon';
import { Typography } from '@kizen/kds/Typography';
import { CSSProperties, useMemo } from 'react';
import { Spacer } from '@kizen/kds/Spacer';
import { getUnifiedPlan, interpolateValues } from '../util';

type ComparisonProps = {
  plans: BillingLicensePlans['license_templates'];
  labels: BillingLicensePlans['feature_labels'];
  labelKey: 'general' | 'automation' | 'marketing';
};

type CMSLabel = {
  key: string;
  label: string;
  type: 'boolean' | 'number' | 'string';
};

export const Comparison = (props: ComparisonProps) => {
  const { plans, labels, labelKey } = props;

  const sectionLabels = useMemo(() => {
    return (labels[labelKey] ?? []) as Array<CMSLabel>;
  }, [labels, labelKey]);

  return (
    <div className="w-full">
      <Spacer size={20} />
      <table className="w-full">
        <thead>
          <tr>
            <th
              className="w-[calc(100%/var(--columns))] text-center h-16"
              style={
                {
                  '--columns': plans.length + 1,
                } as CSSProperties
              }
            ></th>
            {plans.map((plan) => {
              return (
                <th
                  key={plan.id}
                  className="w-[calc(100%/var(--columns))] text-center h-16"
                  style={
                    {
                      '--columns': plans.length + 1,
                    } as CSSProperties
                  }
                >
                  <Typography size="lg" weight="semibold">
                    {plan.published_name}
                  </Typography>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sectionLabels.map((label, index) => {
            return (
              <tr
                key={label.key}
                className="bg-[var(--bg)]"
                style={
                  {
                    '--bg':
                      index % 2 === 0
                        ? 'var(--color-background-default)'
                        : 'white',
                  } as CSSProperties
                }
              >
                <td
                  className="w-[calc(100%/var(--columns))] text-left p-6"
                  style={
                    {
                      '--columns': plans.length + 1,
                      '--bg':
                        index % 2 === 0
                          ? 'var(--color-background-default)'
                          : 'white',
                    } as CSSProperties
                  }
                >
                  <Typography weight="semibold">{label.label}</Typography>
                </td>
                {plans.map((plan) => {
                  const additionalFeatures = (plan.ui_config
                    ?.additional_features ?? {}) as Record<string, unknown>;

                  return (
                    <td
                      key={plan.id}
                      className="w-[calc(100%/var(--columns))] p-4 items-center text-center"
                      style={
                        {
                          '--columns': plans.length + 1,
                        } as CSSProperties
                      }
                    >
                      {label.type === 'boolean' ? (
                        <div className="flex justify-center">
                          {additionalFeatures[label.key] ? (
                            <Icon
                              icon="custom-circle-check"
                              size="xl"
                              className="text-border-active"
                            />
                          ) : null}
                        </div>
                      ) : label.type === 'string' || label.type === 'number' ? (
                        <div className="min-h-[19px] flex items-center justify-center">
                          <Typography>
                            {interpolateValues(
                              additionalFeatures[label.key] as string,
                              getUnifiedPlan(plan)
                            )}
                          </Typography>
                        </div>
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
